// Model / assistant availability by country — curated quarterly snapshot.
// A cell is only marked when a source makes the claim; everything else is "Unknown".
export type Availability = 'Available' | 'Restricted' | 'Blocked' | 'Unknown';

export interface AvailabilityCell {
  status: Exclude<Availability, 'Unknown'>;
  sourceId?: string;
  href?: string;
  note: string;
}

export interface AvailabilityRow {
  model: string;
  product: string;
  cells: Partial<Record<string, AvailabilityCell>>;
}

export const AVAILABILITY_AS_OF = 'Q4 2026 · Oct 5, 2026';

export const matrixCountries = ['USA', 'CHN', 'IND', 'GBR', 'DEU', 'FRA', 'ITA', 'JPN', 'KOR', 'BRA', 'IDN', 'RUS', 'ARE', 'CAN', 'AUS'] as const;

const listed = (sourceId: string, codes: string[], note: string): Partial<Record<string, AvailabilityCell>> =>
  Object.fromEntries(codes.map((c) => [c, { status: 'Available' as const, sourceId, note }]));

const SUPPORTED_15 = ['USA', 'IND', 'GBR', 'DEU', 'FRA', 'ITA', 'JPN', 'KOR', 'BRA', 'IDN', 'ARE', 'CAN', 'AUS'];
const EU = ['DEU', 'FRA', 'ITA'];

export const availability: AvailabilityRow[] = [
  {
    model: 'GPT-5',
    product: 'ChatGPT / OpenAI API',
    cells: {
      ...listed('openaiCountries', SUPPORTED_15, 'On OpenAI’s supported-countries list'),
      CHN: { status: 'Blocked', sourceId: 'openaiCountries', note: 'Not a supported country' },
      RUS: { status: 'Blocked', sourceId: 'openaiCountries', note: 'Not a supported country' },
    },
  },
  {
    model: 'gpt-oss-120b',
    product: 'Open weights',
    cells: { CHN: { status: 'Available', href: 'https://openai.com/index/introducing-gpt-oss/', note: 'Apache 2.0 open weights — downloadable anywhere the host is reachable' } },
  },
  {
    model: 'Claude Opus 4.5',
    product: 'Claude.ai / Anthropic API',
    cells: {
      ...listed('anthropicCountries', SUPPORTED_15, 'On Anthropic’s supported-regions list'),
      CHN: { status: 'Blocked', sourceId: 'anthropicCountries', note: 'Not a supported region' },
      RUS: { status: 'Blocked', sourceId: 'anthropicCountries', note: 'Not a supported region' },
    },
  },
  {
    model: 'Claude Sonnet 4.5',
    product: 'Claude.ai / Anthropic API',
    cells: {
      ...listed('anthropicCountries', SUPPORTED_15, 'On Anthropic’s supported-regions list'),
      CHN: { status: 'Blocked', sourceId: 'anthropicCountries', note: 'Not a supported region' },
      RUS: { status: 'Blocked', sourceId: 'anthropicCountries', note: 'Not a supported region' },
    },
  },
  {
    model: 'Gemini 3 Pro',
    product: 'Gemini app',
    cells: {
      ...listed('geminiCountries', SUPPORTED_15, 'Listed where Gemini Apps are available'),
      CHN: { status: 'Blocked', sourceId: 'geminiCountries', note: 'Not listed; Google services blocked' },
      RUS: { status: 'Blocked', sourceId: 'geminiCountries', note: 'Not listed' },
    },
  },
  {
    model: 'Gemma 3 27B',
    product: 'Open weights',
    cells: {},
  },
  {
    model: 'Grok 4',
    product: 'Grok on X',
    cells: {
      CHN: { status: 'Blocked', sourceId: 'wikiTwitterCensorship', note: 'X is blocked in mainland China' },
      RUS: { status: 'Blocked', sourceId: 'wikiTwitterCensorship', note: 'X blocked in Russia since 2022' },
    },
  },
  {
    model: 'Llama 4 Maverick',
    product: 'Meta AI / open weights',
    cells: {
      ...Object.fromEntries(EU.map((c) => [c, { status: 'Restricted' as const, sourceId: 'metaAiEu', note: 'Meta AI arrived in the EU only in March 2025, with a reduced feature set' }])),
      CHN: { status: 'Blocked', sourceId: 'wikiGreatFirewall', note: 'Meta services blocked by the Great Firewall' },
    },
  },
  {
    model: 'DeepSeek-V3.2 / R1',
    product: 'DeepSeek app & API',
    cells: {
      CHN: { status: 'Available', sourceId: 'cacRegistry', note: 'Filed generative-AI service in China' },
      ITA: { status: 'Blocked', sourceId: 'wikiDeepSeek', note: 'Garante ordered the app blocked (Jan 2025)' },
      KOR: { status: 'Restricted', sourceId: 'wikiDeepSeek', note: 'New app downloads suspended (Feb 2025); government-device bans' },
      USA: { status: 'Restricted', sourceId: 'wikiDeepSeek', note: 'Banned on many federal and state government devices' },
      AUS: { status: 'Restricted', sourceId: 'wikiDeepSeek', note: 'Banned on government devices (Feb 2025)' },
      IND: { status: 'Restricted', sourceId: 'wikiDeepSeek', note: 'Finance ministry advised against use on office devices' },
      DEU: { status: 'Restricted', sourceId: 'wikiDeepSeek', note: 'Data-protection commissioner asked app stores to delist (2025)' },
    },
  },
  {
    model: 'Qwen3',
    product: 'Qwen Chat / open weights',
    cells: { CHN: { status: 'Available', sourceId: 'cacRegistry', note: 'Filed generative-AI service in China' } },
  },
  {
    model: 'Doubao',
    product: 'Doubao app',
    cells: { CHN: { status: 'Available', sourceId: 'cacRegistry', note: 'Filed generative-AI service in China' } },
  },
  {
    model: 'Kimi K2',
    product: 'Kimi app / open weights',
    cells: { CHN: { status: 'Available', sourceId: 'cacRegistry', note: 'Filed generative-AI service in China' } },
  },
  {
    model: 'Mistral Medium 3',
    product: 'Le Chat / La Plateforme',
    cells: { FRA: { status: 'Available', href: 'https://mistral.ai', note: 'Home market of Mistral AI' } },
  },
  {
    model: 'Apple Foundation Models',
    product: 'Apple Intelligence',
    cells: {
      USA: { status: 'Available', sourceId: 'appleIntelligence', note: 'Listed on Apple’s availability page' },
      CHN: { status: 'Restricted', sourceId: 'appleIntelligence', note: 'Not available in mainland China' },
    },
  },
];
